/**
 * Seat allocation rule: the first N attendees (by index) take the available
 * seats; everyone after spills onto the waitlist. Centralizing this here keeps
 * all callsites in lockstep with the backend's expectation.
 */
export function computeIsWaitlist(
  attendeeIndex: number,
  availableSeats: number,
): boolean {
  return attendeeIndex >= availableSeats;
}

export function computeWaitlistedCount(
  selectedAttendeeCount: number,
  availableSeats: number,
): number {
  return Math.max(0, selectedAttendeeCount - availableSeats);
}

export function computeConfirmedCount(
  selectedAttendeeCount: number,
  availableSeats: number,
): number {
  return Math.min(selectedAttendeeCount, availableSeats);
}

/**
 * Recompute `isWaitlist` on a list of attendees based on each *selected*
 * attendee's position relative to the available-seats cap. Unselected
 * attendees always come back with `isWaitlist: false` (they're not even
 * in the running).
 *
 * Used to refresh stale waitlist state when moving from the review step
 * into the payment step — the review UI displays a Waitlist badge from
 * index, but the underlying flag can drift if attendees were added
 * before availableSeats data settled.
 */
export function recomputeAttendeeWaitlistFlags<
  T extends { isSelected: boolean; isWaitlist: boolean },
>(attendees: T[], availableSeats: number): T[] {
  let selectedIndex = 0;
  return attendees.map((attendee) => {
    if (!attendee.isSelected) {
      return { ...attendee, isWaitlist: false };
    }
    const isWaitlist = computeIsWaitlist(selectedIndex, availableSeats);
    selectedIndex++;
    return { ...attendee, isWaitlist };
  });
}
